/**
 * Country pricing/support config for myaccount-utils-static.
 * Values mirror libs/domain-layer/src/js/value-objects/country-config/country-config.ts (CBU-21106).
 * This copy avoids a domain-layer import (circular dependency with infrastructure observability).
 */
import PlanGroupIds from './planGroupIds';

export interface CountryConfig {
  countryCode: string;
  currency: string;
  currencySymbol: string;
  inboxPreviewAddOnId: string;
  supportPhone: string | null;
  planGroups: number[];
}

export const COUNTRY_CONFIGS: Record<string, CountryConfig> = {
  US: {
    countryCode: 'US',
    currency: 'USD',
    currencySymbol: '$',
    inboxPreviewAddOnId: 'CTCT-INBOX-PREVIEW-101',
    supportPhone: '855-229-5506',
    planGroups: [],
  },
  AU: {
    countryCode: 'AU',
    currency: 'AUD',
    currencySymbol: 'A$',
    inboxPreviewAddOnId: 'CTCT-INBOX-PREVIEW-101',
    supportPhone: '1800-290-803',
    planGroups: [PlanGroupIds.AUSTRALIA_V1],
  },
  GB: {
    countryCode: 'GB',
    currency: 'GBP',
    currencySymbol: '£',
    inboxPreviewAddOnId: 'CTCT-INBOX-PREVIEW-101',
    // null until CBU-21269; getSupportPhoneNumber falls back to the US number
    supportPhone: null,
    planGroups: [PlanGroupIds.GREAT_BRITAIN_V1, PlanGroupIds.GREAT_BRITAIN_LEAD_MAGNET],
  },
};

export const getCountryConfig = (countryCode?: string): CountryConfig => {
  const normalized = countryCode?.toUpperCase();

  return COUNTRY_CONFIGS[normalized ?? ''] ?? COUNTRY_CONFIGS.US;
};

export const getCountryConfigByPlanGroup = (planGroupId?: number): CountryConfig | undefined => {
  if (planGroupId == null) return undefined;

  return Object.values(COUNTRY_CONFIGS).find((config) => config.planGroups.includes(planGroupId));
};

const getNumberFormatLocale = (): string => (typeof navigator !== 'undefined' ? navigator.language : 'en-US');

export const formatCompactPrice = (priceAmount: number, currency: string): string => {
  const isWholeAmount = priceAmount % 1 === 0;

  return new Intl.NumberFormat(getNumberFormatLocale(), {
    style: 'currency',
    currency,
    minimumFractionDigits: isWholeAmount ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(priceAmount);
};

export const formatPrice = (priceAmount: number, currency: string, minimumFractionDigits = 2): string =>
  new Intl.NumberFormat(getNumberFormatLocale(), { style: 'currency', currency, minimumFractionDigits }).format(
    priceAmount
  );
